import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getUsers, updateUserStatus, getRoles } from '../services/iam';
import { User, Role } from '../types/iam';
import { toast } from '@/lib/toast';
import ManageUserRolesModal from '../components/modals/ManageUserRolesModal';
import DeleteUserModal from '../components/modals/DeleteUserModal';
import UpdateUserModal from '../components/modals/UpdateUserModal';
import AddUserModal from '../components/modals/AddUserModal';
import InviteUserModal from '../components/modals/InviteUserModal';
import InvitesPanel from '../components/InvitesPanel';
import { AlertTriangle, IdCard, Mail, Pencil, RefreshCw, SearchX, Trash2, UserCheck, UserPlus, UserX, UsersRound } from 'lucide-react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import PermissionGate from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { Button, Card, EmptyState, IconButton, InitialsAvatar, OutcomeBadge, PageHeader, PaginationBar, SearchField, Tabs, TanstackTable } from '@/components/ds-zta';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { userStatusValue } from '@/lib/userStatus';
import { useUrlState } from '@/lib/useUrlState';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { ROWS_PER_PAGE_OPTIONS, pageOffset, readPage, readRows } from '@/lib/urlPaging';

const columnHelper = createColumnHelper<User>();

const UsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { PERMISSIONS, isAdmin } = usePermissions();
  const { confirm: confirmDialog } = useDialogs();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToUpdate, setUserToUpdate] = useState<User | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  // Search and paging live in the URL (design eval F24); the search goes to
  // the server as `q` (F7), and a new search starts again at page 1.
  // The Invites tab (admins only) keeps its own filter and paging in the URL.
  const [url, setUrl] = useUrlState({ tab: 'members', q: '', page: '1', rows: '20', istatus: 'pending', ipage: '1', irows: '20' });
  const activeTab = url.tab === 'invites' && isAdmin() ? 'invites' : 'members';
  const page = readPage(url.page);
  const itemsPerPage = readRows(url.rows);
  const q = useDebouncedValue(url.q.trim());

  const { data: usersResponse, isLoading, isError, error, refetch } = useQuery(
    ['users', q, page, itemsPerPage],
    async () => {
      const response = await getUsers({ q: q || undefined, limit: itemsPerPage, offset: pageOffset(page, itemsPerPage) });
      return response;
    },
    { keepPreviousData: true, enabled: activeTab === 'members' }
  );

  // ['roles', 'all']: every role (the name lookup for the Primary Role column).
  // ['roles', 'primary'] is the primary-only list the user modals offer.
  const { data: rolesResponse } = useQuery(
    ['roles', 'all'],
    async () => {
      const response = await getRoles();
      return response;
    }
  );

  const rolesMap = useMemo(() => {
    const map = new Map<string, Role>();
    (rolesResponse?.data || []).forEach(role => {
      map.set(role.id, role);
    });
    return map;
  }, [rolesResponse]);

  const mutation = useMutation(
    (user: User) => {
      const newStatus = user.status === 'active' ? 'suspended' : 'active';
      return updateUserStatus(user.id, newStatus);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('User status updated');
      },
      onError: (err: any) => {
        toast.error(`Failed to update user: ${err.message}`);
      },
    }
  );


  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        meta: { sortable: true, sortValue: (u) => (u.name || u.email || '').toLowerCase() },
        cell: (info) => {
          const user = info.row.original;
          return (
            <div className="flex items-center">
              <InitialsAvatar name={user.name} email={user.email} src={user.picture_url} />
              <div className="ml-4">
                <Link
                  to={`/users/${user.id}`}
                  className="text-dense font-medium text-[var(--d2d-text-primary)] hover:underline"
                >
                  {info.getValue()}
                </Link>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        meta: { sortable: true },
        cell: (info) => (
          <span className="text-dense text-[var(--d2d-text-muted)]">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        meta: { sortable: true },
        cell: (info) => {
          const status = info.getValue();
          return (
            <OutcomeBadge value={userStatusValue(status)}>
              {status}
            </OutcomeBadge>
          );
        },
      }),
      columnHelper.accessor('primary_role', {
        header: 'Primary Role',
        meta: {
          sortable: true,
          sortValue: (u) => (u.primary_role ? rolesMap.get(u.primary_role)?.name ?? u.primary_role : '').toLowerCase(),
        },
        cell: (info) => {
          const user = info.row.original;
          const primaryRoleId = info.getValue();
          const role = primaryRoleId ? rolesMap.get(primaryRoleId) : null;
          return (
            <div className="flex items-center gap-2">
              <span className="text-dense text-[var(--d2d-text-primary)]">
                {role ? role.name : (primaryRoleId ? primaryRoleId : 'Not Set')}
              </span>
              <PermissionGate permissions={[PERMISSIONS.ROLE_BINDINGS_CREATE, PERMISSIONS.ROLE_BINDINGS_DELETE]}>
                <IconButton
                  size="sm"
                  aria-label="Edit Primary Role"
                  tooltip="Edit Primary Role"
                  icon={<Pencil className="h-4 w-4" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setUserToUpdate(user);
                  }}
                />
              </PermissionGate>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="block text-right">Actions</span>,
        cell: (info) => {
          const user = info.row.original;
          const isActive = user.status === 'active';
          return (
            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
              <PermissionGate permission={PERMISSIONS.USERS_UPDATE_STATUS}>
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                  onClick={async () => {
                    const label = user.name || user.email;
                    const ok = await confirmDialog(
                      isActive
                        ? {
                            title: `Suspend ${label}?`,
                            message: `${label} (${user.email}) will lose access until an administrator activates the account again.`,
                            confirmLabel: 'Suspend',
                            variant: 'danger',
                          }
                        : {
                            title: `Activate ${label}?`,
                            message: `${label} (${user.email}) will be able to sign in and use their assigned roles again.`,
                            confirmLabel: 'Activate',
                            variant: 'warning',
                          }
                    );
                    if (ok) mutation.mutate(user);
                  }}
                  disabled={mutation.isLoading}
                >
                  {isActive ? 'Suspend' : 'Activate'}
                </Button>
              </PermissionGate>
              <PermissionGate permissions={[PERMISSIONS.ROLE_BINDINGS_CREATE, PERMISSIONS.ROLE_BINDINGS_DELETE]}>
                <IconButton
                  size="sm"
                  aria-label="Manage Other Roles"
                  tooltip="Manage other roles"
                  icon={<IdCard className="h-4 w-4" />}
                  onClick={() => {
                    setSelectedUser(user);
                  }}
                />
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.USERS_DELETE}>
                <IconButton
                  size="sm"
                  aria-label="Delete user"
                  tooltip="Delete user"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={() => {
                    setUserToDelete(user);
                  }}
                />
              </PermissionGate>
            </div>
          );
        },
      }),
    ],
    [mutation.isLoading, rolesMap, PERMISSIONS, confirmDialog]
  );

  const users = usersResponse?.data || [];
  const total = usersResponse?.total || 0;

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePageChange = (newPage: number) => {
    setUrl({ page: newPage });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setUrl({ rows: newItemsPerPage, page: null }); // Reset to first page when changing items per page
  };

  const emptyState = isError ? (
    <EmptyState
      icon={<AlertTriangle />}
      title="Couldn't load users"
      description={
        (error as any)?.response?.data?.error ||
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'Something went wrong while loading this list.'
      }
      action={
        <Button size="sm" variant="secondary" leadingIcon={<RefreshCw className="h-4 w-4" />} onClick={() => refetch()}>
          Retry
        </Button>
      }
    />
  ) : q ? (
    <EmptyState
      icon={<SearchX />}
      title={`No users match "${q}"`}
      description="Try a different name or email."
      action={
        <Button size="sm" variant="secondary" onClick={() => setUrl({ q: null, page: null })}>
          Clear search
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<UsersRound />}
      title="No users yet"
      description="Users appear here once an administrator adds or invites them and they sign in."
      action={
        isAdmin() ? (
          <Button size="sm" leadingIcon={<UserPlus className="h-4 w-4" />} onClick={() => setShowAddUser(true)}>
            Add User
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={<UsersRound />}
        title="User Management"
        description="Manage user accounts and their access permissions."
        primaryAction={
          isAdmin() ? (
            <div className="flex gap-2">
              <Button variant="secondary" leadingIcon={<Mail className="h-4 w-4" />} onClick={() => setShowInvite(true)}>
                Invite User
              </Button>
              <Button leadingIcon={<UserPlus className="h-4 w-4" />} onClick={() => setShowAddUser(true)}>
                Add User
              </Button>
            </div>
          ) : undefined
        }
      />
      {isAdmin() && (
        <Tabs
          value={activeTab}
          urlParam="tab"
          onChange={(v) => setUrl({ tab: v })}
          aria-label="User management views"
        >
          <Tabs.List>
            <Tabs.Tab value="members">
              <span className="flex items-center gap-2 whitespace-nowrap">
                <UsersRound className="h-4 w-4" aria-hidden="true" />
                Members
              </span>
            </Tabs.Tab>
            <Tabs.Tab value="invites">
              <span className="flex items-center gap-2 whitespace-nowrap">
                <Mail className="h-4 w-4" aria-hidden="true" />
                Invites
              </span>
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      )}
      {activeTab === 'invites' ? (
        <InvitesPanel
          status={url.istatus}
          page={readPage(url.ipage)}
          rows={readRows(url.irows)}
          onStatusChange={(v) => setUrl({ istatus: v, ipage: null })}
          onPageChange={(p) => setUrl({ ipage: p })}
          onRowsChange={(r) => setUrl({ irows: r, ipage: null })}
          onInvite={() => setShowInvite(true)}
        />
      ) : (
      <>
      <div className="max-w-md">
        <SearchField
          label="Search users"
          placeholder="Search by name or email…"
          value={url.q}
          onChange={(next) => setUrl({ q: next, page: null }, { replace: true })}
        />
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <TanstackTable aria-label="Users" table={table} loading={isLoading} empty={emptyState} />
        </div>
      </Card>
      {/* On total, not this page's rows: a linked page past the end still offers the way back. */}
      {(total || users.length) > 0 && (
        <div>
          {<PaginationBar
            page={page}
            totalRows={total || users.length}
            rowsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleItemsPerPageChange}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />}
        </div>
      )}
      </>
      )}

      {selectedUser && (
        <ManageUserRolesModal
          userId={selectedUser.id}
          userName={selectedUser.name}
          onClose={() => setSelectedUser(null)}
        />
      )}

      {userToDelete && (
        <DeleteUserModal
          user={userToDelete}
          onClose={() => setUserToDelete(null)}
        />
      )}

      {userToUpdate && (
        <UpdateUserModal
          user={userToUpdate}
          onClose={() => setUserToUpdate(null)}
        />
      )}

      {showAddUser && (
        <AddUserModal onClose={() => setShowAddUser(false)} />
      )}

      {showInvite && (
        <InviteUserModal
          onClose={() => {
            setShowInvite(false);
            setUrl({ tab: 'invites', istatus: 'pending', ipage: null });
          }}
        />
      )}
    </div>
  );
};

export default UsersPage;
